/**
 * Abstract base class for creating store implementations
 * that can be used with React's useSyncExternalStore hook
 */
export default abstract class AbstractStore<TState, TAction> {
  // State stored by the store
  protected state: TState;

  // Set of listeners to be called when state changes
  private listeners: Set<() => void> = new Set();

  // Constructor accepts initial state
  constructor(initialState: TState) {
    this.state = initialState;
  }

  // Getter to retrieve current state
  getState = (): TState => {
    return this.state;
  };

  // Method to register a listener and returns unsubscribe function
  // This is compatible with useSyncExternalStore
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);

    // Return a function to unsubscribe
    return () => {
      this.listeners.delete(listener);
    };
  };

  // Protected method to update state and notify all listeners
  protected setState = (newState: TState): void => {
    this.state = { ...(newState as object) } as TState;

    // Notify all listeners
    this.listeners.forEach((listener) => listener());
  };

  // Array to store middleware functions - optional feature
  private middlewares?: ((state: TState, action: TAction) => void)[];

  // Method to add middleware - optional
  addMiddleware(middleware: (state: TState, action: TAction) => void) {
    if (!this.middlewares) {
      this.middlewares = [];
    }
    this.middlewares.push(middleware);

    return this;
  }

  // Abstract method that must be implemented by subclasses
  abstract dispatch(action: TAction): void;

  // Protected method to run all middlewares (if any exist)
  protected runMiddlewares(action: TAction): void {
    if (this.middlewares && this.middlewares.length > 0) {
      this.middlewares.forEach((middleware) => middleware(this.state, action));
    }
  }
}
